import { useEffect, useState } from 'react'
import { Loader2, Package, Check, Truck } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { useGetOrderTotes, useCheckInTote, useMarkOrderFulfilled } from '../../hooks/backend/scanPage'
import { toast } from '../../lib/shadcn/sonner'
import { cn } from '../../lib/shadcn/utils'
import { fulfillmentSyncWarning } from '../../utils/syncResults'

interface Tote {
  id: string
  customer: string | null
}

interface Props {
  orderId: string | null
  customer: string
  onOpenChange: (open: boolean) => void
  onDelivered: () => void
}

export default function DeliverOrderDialog({ orderId, customer, onOpenChange, onDelivered }: Props) {
  const getOrderTotes = useGetOrderTotes()
  const checkInTote = useCheckInTote()
  const markOrderFulfilled = useMarkOrderFulfilled()

  const [totes, setTotes] = useState<Tote[]>([])
  const [loading, setLoading] = useState(false)
  const [checkingInId, setCheckingInId] = useState<string | null>(null)
  const [checkingInAll, setCheckingInAll] = useState(false)
  const [delivering, setDelivering] = useState(false)

  useEffect(() => {
    if (!orderId) return
    setLoading(true)
    setTotes([])
    getOrderTotes
      .trigger({ order_id: orderId })
      .then((data: unknown) => setTotes(((data ?? []) as Tote[])))
      .catch(() => toast.error('Failed to load checked-out totes'))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId])

  const busy = checkingInId !== null || checkingInAll || delivering

  const handleCheckIn = async (toteId: string) => {
    setCheckingInId(toteId)
    try {
      await checkInTote.trigger({ tote_id: toteId })
      setTotes(prev => prev.filter(t => t.id !== toteId))
      toast.success(`Tote ${toteId} checked in`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : `Failed to check in tote ${toteId}`)
    } finally {
      setCheckingInId(null)
    }
  }

  const handleCheckInAll = async () => {
    setCheckingInAll(true)
    try {
      for (const tote of totes) {
        await checkInTote.trigger({ tote_id: tote.id })
      }
      setTotes([])
      toast.success('All totes checked in')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to check in totes')
    } finally {
      setCheckingInAll(false)
    }
  }

  const handleDeliver = async () => {
    if (!orderId) return
    setDelivering(true)
    try {
      const result = await markOrderFulfilled.trigger({ order_id: orderId, status: 'delivered' })
      toast.success(`Order ${orderId} marked delivered`)
      const warning = fulfillmentSyncWarning((result as { sync_results?: unknown } | undefined)?.sync_results)
      if (warning) toast.warning(warning)
      onDelivered()
      onOpenChange(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to mark delivered')
    } finally {
      setDelivering(false)
    }
  }

  return (
    <Dialog
      open={orderId !== null}
      onOpenChange={next => {
        if (!next && !busy) onOpenChange(false)
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Mark order {orderId} delivered</DialogTitle>
          <DialogDescription>
            {customer || 'Customer'} — check in any totes returned with this delivery, then confirm.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">
              Checked-out totes {loading ? '' : `(${totes.length})`}
            </span>
            {totes.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => void handleCheckInAll()}
              >
                {checkingInAll ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                Check In All
              </Button>
            )}
          </div>

          <div className="max-h-[280px] overflow-y-auto rounded-md border divide-y">
            {loading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : totes.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                No checked-out totes for this customer.
              </div>
            ) : (
              totes.map(tote => (
                <div key={tote.id} className="flex items-center justify-between gap-2 px-3 py-2">
                  <span className="flex items-center gap-2 text-sm text-foreground">
                    <Package className="w-4 h-4 text-muted-foreground" />
                    <span className="font-mono">{tote.id}</span>
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn('h-7 px-2 text-xs text-muted-foreground hover:text-foreground')}
                    disabled={busy}
                    onClick={() => void handleCheckIn(tote.id)}
                  >
                    {checkingInId === tote.id ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <Check className="w-3 h-3 mr-1" />
                    )}
                    Check In
                  </Button>
                </div>
              ))
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => void handleDeliver()} disabled={busy}>
            {delivering ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <Truck className="w-4 h-4 mr-1" />
            )}
            Mark Delivered
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
